import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import {
    Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, LinearProgress,
    MenuItem, Snackbar, Table, TableBody, TableCell, TableHead, TableRow, TextField,
} from '@mui/material'
import { H3 } from '../../layouts/TextStyles'
import { ui } from '../../utils/uiTokens'
import { capitalize } from '../../utils/functions'
import { SURVEY_TYPES, Survey, SurveyBody, SurveyKind, SurveyQuestion, SurveyType, surveyApi } from '../../features/survey/surveyApi'
import { Pill, bodyRow, btn, headRow, muted, page, sub, tableWrap } from '../director/ui'
import { addDays, addMonths, ddmm, templateQuestions, todayAlmaty } from './surveyTemplates'

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const LANGS = ['KZ', 'RU', '']

const typeOf = (s: Survey): SurveyType => s.type ?? 'MENTOR'
const hasCourse = (t: SurveyType) => t === 'MENTOR' || t === 'TEACHER'

const emptyQuestion = (kind: SurveyKind = 'RATING'): SurveyQuestion => ({
    kind,
    textKz: '',
    textRu: '',
    required: true,
    options: kind === 'CHOICE' ? [{ textKz: '', textRu: '' }, { textKz: '', textRu: '' }] : [],
})

const toBody = (s: Survey): SurveyBody => ({
    titleKz: s.titleKz ?? '',
    titleRu: s.titleRu ?? '',
    courseId: s.courseId,
    subjectId: s.subjectId,
    monthNumber: s.monthNumber,
    openDate: s.openDate,
    closeDate: s.closeDate,
    questions: s.questions.map((q) => ({ ...q, textKz: q.textKz ?? '', textRu: q.textRu ?? '' })),
    groupIds: s.groupIds ?? [],
    type: typeOf(s),
    language: s.language,
    subjectHeadUserId: s.subjectHeadUserId,
    teacherUserId: s.teacherUserId,
})

// «Актуальды»: сначала открытые заново, потом ближайший срок; без дат - в конце
const cmpActive = (a: Survey, b: Survey) =>
    (b.reopenedAt ?? '').localeCompare(a.reopenedAt ?? '') ||
    (a.closeDate ?? '9999').localeCompare(b.closeDate ?? '9999')
const cmpDone = (a: Survey, b: Survey) => (b.closeDate ?? '').localeCompare(a.closeDate ?? '')

export const btnPink = { ...btn, background: ui.pink, color: ui.card, '&:hover': { background: ui.pinkDark }, '&.Mui-disabled': { background: ui.track, color: ui.muted } }

export const TypeTabs: React.FC<{ types: SurveyType[]; value: SurveyType; count?: (t: SurveyType) => number; onChange: (t: SurveyType) => void }> = ({
    types, value, count, onChange,
}) => {
    const { formatMessage } = useIntl()
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', marginBottom: '12px' }}>
            {types.map((t) => (
                <Button key={t} size="small" variant={t === value ? 'contained' : 'outlined'} onClick={() => onChange(t)}
                    sx={t === value ? btnPink : { ...btn, color: ui.ink, borderColor: ui.border }}>
                    {formatMessage({ id: `surveyType_${t}` })}
                    {count && <Box component="span" sx={{ marginLeft: '6px', opacity: 0.7, fontWeight: 600 }}>{count(t)}</Box>}
                </Button>
            ))}
        </Box>
    )
}

const SurveysPage = () => {
    const { formatMessage, locale } = useIntl()
    const navigate = useNavigate()
    const L = capitalize(locale)
    const { data, isFetching } = surveyApi.useGetSurveysQuery()
    const [del] = surveyApi.useDeleteStudentSurveyMutation()
    const [restore] = surveyApi.useRestoreStudentSurveyMutation()
    const [type, setType] = useState<SurveyType>('MENTOR')
    const [tab, setTab] = useState<'active' | 'done'>('active')
    const [editing, setEditing] = useState<{ id: number | null; locked: boolean; body: SurveyBody } | null>(null)
    const [reopening, setReopening] = useState<Survey | null>(null)
    const [deleting, setDeleting] = useState<Survey | null>(null)
    const [undo, setUndo] = useState<Survey | null>(null)

    const surveys = data?.surveys ?? []
    const canEdit = !!data?.canEdit
    const ofType = surveys.filter((s) => typeOf(s) === type)
    const active = ofType.filter((s) => !s.finished).sort(cmpActive)
    const done = ofType.filter((s) => s.finished).sort(cmpDone)
    const rows = tab === 'active' ? active : done
    const today = todayAlmaty()

    const createNew = () => {
        let openDate = today
        let closeDate = addDays(today, 3)
        // NPS раз в 3 месяца: следующий через 3 месяца после последнего
        if (type === 'NPS') {
            const last = surveys.filter((s) => typeOf(s) === 'NPS' && s.closeDate).map((s) => s.closeDate as string).sort().pop()
            closeDate = last ? addMonths(last, 3) : addDays(today, 7)
            openDate = last ? addDays(closeDate, -7) : today
        }
        setEditing({
            id: null,
            locked: false,
            body: {
                titleKz: '', titleRu: '', courseId: null, subjectId: null, monthNumber: null, openDate, closeDate,
                questions: templateQuestions(type), groupIds: [], type, language: null, subjectHeadUserId: null,
                teacherUserId: null,
            },
        })
    }

    const onDelete = async (s: Survey) => {
        setDeleting(null)
        try {
            await del(s.id).unwrap()
            setUndo(s)
        } catch { /* общий обработчик */ }
    }

    const onUndo = async () => {
        if (!undo) return
        const s = undo
        setUndo(null)
        try {
            await restore(s.id).unwrap()
        } catch { /* общий обработчик */ }
    }

    const status = (s: Survey) => {
        if (s.finished) return <Pill tone="mute">{formatMessage({ id: 'surveyStatusDone' })}</Pill>
        if (!s.openDate || !s.closeDate) return <Pill tone="mute">{formatMessage({ id: 'surveyStatusNoDates' })}</Pill>
        if (s.openDate > today)
            return (
                <>
                    <Pill tone="info">{formatMessage({ id: 'surveyStatusPlanned' })}</Pill>
                    <Box sx={muted}>{formatMessage({ id: 'surveyOpensOn' }, { date: ddmm(s.openDate) })}</Box>
                </>
            )
        return (
            <>
                <Pill tone={s.closeDate === today ? 'warn' : 'good'}>{formatMessage({ id: 'surveyStatusOpen' })}</Pill>
                {s.closeDate === today && <Box sx={muted}>{formatMessage({ id: 'surveyClosesToday' })}</Box>}
            </>
        )
    }

    const target = (s: Survey) => {
        if (typeOf(s) === 'SUBJECT_HEAD') {
            const name = `${s.subjectHeadFirstName ?? ''} ${s.subjectHeadLastName ?? ''}`.trim()
            return (
                <>
                    {formatMessage({ id: 'surveyHeadLabel' })}: {name || '-'}
                    <Box sx={muted}>{(s as any)[`subjectTitle${L}`] || s.subjectTitleRu}</Box>
                </>
            )
        }
        if (typeOf(s) === 'NPS') return formatMessage({ id: 'surveyAllStudents' })
        const meta = [
            (s as any)[`subjectTitle${L}`] || formatMessage({ id: 'surveyAllSubjects' }),
            typeOf(s) === 'TEACHER' ? `${s.teacherFirstName ?? ''} ${s.teacherLastName ?? ''}`.trim() || null : null,
            typeOf(s) === 'TEACHER' ? s.language || 'KZ + RU' : null,
            s.groupIds?.length ? formatMessage({ id: 'surveyGroupsN' }, { n: s.groupIds.length }) : null,
        ].filter(Boolean).join(' · ')
        return (
            <>
                {(s as any)[`courseTitle${L}`] || <span style={{ color: ui.muted }}>{formatMessage({ id: 'surveyNoCourse' })}</span>}
                <Box sx={muted}>{meta}</Box>
            </>
        )
    }

    const tabBtn = (k: 'active' | 'done', n: number) => (
        <Button size="small" onClick={() => setTab(k)}
            sx={{ ...btn, borderRadius: 0, color: tab === k ? ui.pink : ui.sub, borderBottom: `2px solid ${tab === k ? ui.pink : 'transparent'}` }}>
            {formatMessage({ id: k === 'active' ? 'surveyListActive' : 'surveyListDone' })}
            <Box component="span" sx={{ marginLeft: '6px', opacity: 0.7 }}>{n}</Box>
        </Button>
    )

    return (
        <Box sx={{ margin: '10px' }}>
            <Box sx={page}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                    <Box>
                        <H3>{formatMessage({ id: 'surveysTitle' })}</H3>
                        <Box sx={{ ...sub, margin: '6px 0 16px', maxWidth: 760 }}>{formatMessage({ id: 'surveysSubTypes' })}</Box>
                    </Box>
                    {canEdit && (
                        <Button variant="contained" sx={btnPink} onClick={createNew}>
                            + {formatMessage({ id: 'surveyNew' })}
                        </Button>
                    )}
                </Box>

                <TypeTabs types={SURVEY_TYPES} value={type} onChange={setType} count={(t) => surveys.filter((s) => typeOf(s) === t).length} />
                <Box sx={{ ...sub, marginBottom: '14px' }}>{formatMessage({ id: `surveyWho_${type}` })}</Box>

                <Box sx={{ display: 'flex', gap: 1, borderBottom: `1px solid ${ui.border}`, marginBottom: '12px' }}>
                    {tabBtn('active', active.length)}
                    {tabBtn('done', done.length)}
                </Box>

                {isFetching && <LinearProgress />}
                <Box sx={tableWrap}>
                    <Table sx={{ minWidth: 960 }}>
                        <TableHead sx={headRow}>
                            <TableRow>
                                <TableCell>{formatMessage({ id: 'surveyColTitle' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'surveyColFiller' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'surveyColTarget' })}</TableCell>
                                <TableCell align="center">{formatMessage({ id: 'surveyColMonth' })}</TableCell>
                                <TableCell align="center">{formatMessage({ id: 'surveyColDates' })}</TableCell>
                                <TableCell align="right">{formatMessage({ id: 'surveyColResponses' })}</TableCell>
                                <TableCell>{formatMessage({ id: 'surveyColStatus' })}</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((s) => (
                                <TableRow key={s.id} hover sx={bodyRow}>
                                    <TableCell>
                                        <b>{(s as any)[`title${L}`] || s.titleRu}</b>
                                        <Box sx={muted}>{formatMessage({ id: 'surveyQuestionsCount' }, { n: s.questions.length })}</Box>
                                    </TableCell>
                                    <TableCell>
                                        <Pill tone="info">{formatMessage({ id: `surveyFiller_${typeOf(s)}` })}</Pill>
                                    </TableCell>
                                    <TableCell>{target(s)}</TableCell>
                                    <TableCell align="center">{s.monthNumber ?? '-'}</TableCell>
                                    <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>{ddmm(s.openDate)} - {ddmm(s.closeDate)}</TableCell>
                                    <TableCell align="right">{s.responseCount}</TableCell>
                                    <TableCell>{status(s)}</TableCell>
                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                        <Box sx={{ display: 'inline-flex', gap: 1, alignItems: 'center' }}>
                                            {canEdit && (
                                                <Button size="small" variant="outlined" sx={{ ...btn, color: ui.ink, borderColor: ui.border }}
                                                    onClick={() => (s.finished ? setReopening(s) : setEditing({ id: s.id, locked: s.locked, body: toBody(s) }))}>
                                                    {formatMessage({ id: s.finished ? 'surveyChangeDates' : 'edit' })}
                                                </Button>
                                            )}
                                            <Button size="small" variant="outlined" sx={{ ...btn, color: ui.pink, borderColor: ui.pinkSoft }}
                                                onClick={() => navigate(`/survey-results?type=${typeOf(s)}&surveyId=${s.id}`)}>
                                                {formatMessage({ id: 'surveyResults' })}
                                            </Button>
                                            {canEdit && !s.isDefault && (
                                                <Button size="small" sx={{ ...btn, color: ui.sub }} onClick={() => setDeleting(s)}>
                                                    {formatMessage({ id: 'surveyDelete' })}
                                                </Button>
                                            )}
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {!isFetching && !rows.length && (
                                <TableRow sx={bodyRow}>
                                    <TableCell colSpan={8} align="center" sx={{ color: ui.muted }}>
                                        {formatMessage({ id: tab === 'active' ? 'surveyNoneActive' : 'surveyNoneDone' })}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </Box>
            </Box>

            {editing && (
                <SurveyEditor key={editing.id ?? 'new'} {...editing} onClose={() => setEditing(null)}
                    onSaved={(t) => { setType(t); setTab('active') }} />
            )}
            {reopening && <ReopenDialog s={reopening} onClose={() => setReopening(null)} onDone={() => setTab('active')} />}

            <Dialog open={!!deleting} onClose={() => setDeleting(null)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700 }}>{formatMessage({ id: 'surveyDeleteTitle' })}</DialogTitle>
                <DialogContent>
                    <Box sx={{ fontWeight: 600, color: ui.ink, marginBottom: '8px' }}>{deleting && ((deleting as any)[`title${L}`] || deleting.titleRu)}</Box>
                    <Box sx={sub}>
                        {deleting && (deleting.responseCount > 0
                            ? formatMessage({ id: 'surveyDeleteWarn' }, { n: deleting.responseCount })
                            : formatMessage({ id: 'surveyDeleteNoAnswers' }))}
                    </Box>
                    <Box sx={{ ...muted, marginTop: '8px' }}>{formatMessage({ id: 'surveyDeleteHint' })}</Box>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button sx={{ ...btn, color: ui.sub }} onClick={() => setDeleting(null)}>{formatMessage({ id: 'cancel' })}</Button>
                    <Button variant="contained" sx={btnPink} onClick={() => deleting && onDelete(deleting)}>{formatMessage({ id: 'surveyDelete' })}</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={!!undo} autoHideDuration={5000} onClose={(_e, reason) => reason !== 'clickaway' && setUndo(null)}
                message={undo ? formatMessage({ id: 'surveyDeleted' }, { title: (undo as any)[`title${L}`] || undo.titleRu }) : ''}
                action={<Button size="small" sx={{ ...btn, color: ui.pinkSoft }} onClick={onUndo}>{formatMessage({ id: 'surveyUndo' })}</Button>} />
        </Box>
    )
}

const ReopenDialog: React.FC<{ s: Survey; onClose: () => void; onDone: () => void }> = ({ s, onClose, onDone }) => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const today = todayAlmaty()
    const [openDate, setOpenDate] = useState(today)
    const [closeDate, setCloseDate] = useState(addDays(today, 2))
    const [reopen, { isLoading }] = surveyApi.useReopenStudentSurveyMutation()
    const err = !openDate || !closeDate
        ? 'surveyErrBothDates'
        : openDate > closeDate
            ? 'surveyErrDateOrder'
            : closeDate < today
                ? 'surveyErrCloseFuture'
                : null

    const submit = async () => {
        try {
            await reopen({ id: s.id, openDate, closeDate }).unwrap()
            toast.success(formatMessage({ id: 'surveyReopened' }))
            onDone()
            onClose()
        } catch { /* общий обработчик */ }
    }

    const target = typeOf(s) === 'SUBJECT_HEAD'
        ? `${s.subjectHeadFirstName ?? ''} ${s.subjectHeadLastName ?? ''}`.trim()
        : (s as any)[`courseTitle${L}`] || s.courseTitleRu || formatMessage({ id: 'surveyAllStudents' })

    return (
        <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>{formatMessage({ id: 'surveyReopenTitle' })}</DialogTitle>
            <DialogContent>
                <Box sx={{ fontWeight: 600, color: ui.ink, marginBottom: '8px' }}>{(s as any)[`title${L}`] || s.titleRu}</Box>
                <Box sx={{ ...sub, background: ui.surface, border: `1px solid ${ui.border}`, borderRadius: ui.radius, padding: '10px 12px', marginBottom: '14px' }}>
                    {formatMessage({ id: 'surveyReopenLocked' }, {
                        q: s.questions.length,
                        target,
                        subject: (s as any)[`subjectTitle${L}`] || s.subjectTitleRu || formatMessage({ id: 'surveyAllSubjects' }),
                        n: s.responseCount,
                    })}
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                    <TextField size="small" type="date" InputLabelProps={{ shrink: true }} label={formatMessage({ id: 'surveyOpenDate' })}
                        value={openDate} onChange={(e) => setOpenDate(e.target.value)} />
                    <TextField size="small" type="date" InputLabelProps={{ shrink: true }} label={formatMessage({ id: 'surveyCloseDate' })}
                        value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
                </Box>
                {err && <Box sx={{ ...muted, color: ui.pinkDark, marginTop: '10px' }}>{formatMessage({ id: err })}</Box>}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button sx={{ ...btn, color: ui.sub }} onClick={onClose}>{formatMessage({ id: 'cancel' })}</Button>
                <Button variant="contained" sx={btnPink} disabled={!!err || isLoading} onClick={submit}>{formatMessage({ id: 'surveyReopenSave' })}</Button>
            </DialogActions>
        </Dialog>
    )
}

const SurveyEditor: React.FC<{
    id: number | null
    locked: boolean
    body: SurveyBody
    onClose: () => void
    onSaved: (t: SurveyType) => void
}> = ({ id, locked, body, onClose, onSaved }) => {
    const { formatMessage, locale } = useIntl()
    const L = capitalize(locale)
    const [form, setForm] = useState<SurveyBody>(body)
    // пока вопросы не трогали, смена типа ставит его үлгі сұрақтар
    const [touched, setTouched] = useState(!!id)
    const { data: options } = surveyApi.useGetSurveyOptionsQuery()
    const [save, { isLoading }] = surveyApi.useSaveStudentSurveyMutation()
    const courses = options?.courses ?? []
    const heads = options?.heads ?? []
    const teachers = [...(options?.teachers ?? [])].sort((a, b) =>
        `${a.lastName ?? ''} ${a.firstName ?? ''}`.localeCompare(`${b.lastName ?? ''} ${b.firstName ?? ''}`))
    const t = form.type

    const course = useMemo(() => courses.find((c) => c.courseId === form.courseId), [courses, form.courseId])
    // кз и ру потоки стартуют в разное время - опрос можно дать только части групп
    const groups = useMemo(() => {
        const seen = new Map<number, string>()
        ;(course?.subjects ?? [])
            .filter((s) => !form.subjectId || s.subjectId === form.subjectId)
            .forEach((s) => s.groups.forEach((g) => seen.set(g.id, g.title ?? `#${g.id}`)))
        return Array.from(seen, ([gid, title]) => ({ id: gid, title }))
    }, [course, form.subjectId])
    const headSubjects = useMemo(() => {
        const seen = new Map<number, string>()
        heads.forEach((h) => seen.set(h.subjectId, (h as any)[`subjectTitle${L}`] || h.subjectTitleRu || `#${h.subjectId}`))
        return Array.from(seen, ([sid, title]) => ({ id: sid, title }))
    }, [heads, L])
    const subjectHeads = heads.filter((h) => h.subjectId === form.subjectId)

    const set = (patch: Partial<SurveyBody>) => setForm((f) => ({ ...f, ...patch }))
    const setQs = (fn: (qs: SurveyQuestion[]) => SurveyQuestion[]) => {
        setTouched(true)
        setForm((f) => ({ ...f, questions: fn(f.questions) }))
    }
    const setQ = (i: number, patch: Partial<SurveyQuestion>) => setQs((qs) => qs.map((q, j) => (j === i ? { ...q, ...patch } : q)))
    const moveQ = (i: number, d: number) =>
        setQs((qs) => {
            const out = [...qs]
            ;[out[i], out[i + d]] = [out[i + d], out[i]]
            return out
        })
    const pickType = (nt: SurveyType) =>
        setForm((f) => ({
            ...f,
            type: nt,
            courseId: null, subjectId: null, groupIds: [], language: null, subjectHeadUserId: null, teacherUserId: null,
            monthNumber: nt === 'NPS' ? null : f.monthNumber,
            questions: touched ? f.questions.filter((q) => nt === 'NPS' || q.kind !== 'NPS') : templateQuestions(nt),
        }))

    const kinds: SurveyKind[] = t === 'NPS' ? ['NPS', 'RATING', 'CHOICE', 'TEXT'] : ['RATING', 'CHOICE', 'TEXT']

    const missing: string[] = []
    if (!form.titleKz.trim()) missing.push('surveyMissTitleKz')
    if (!form.titleRu.trim()) missing.push('surveyMissTitleRu')
    if (hasCourse(t) && !form.courseId) missing.push('surveyMissCourse')
    if (t === 'SUBJECT_HEAD' && !form.subjectId) missing.push('surveyMissSubject')
    if (t === 'SUBJECT_HEAD' && !form.subjectHeadUserId) missing.push('surveyMissHead')
    if (t === 'TEACHER' && !form.teacherUserId) missing.push('surveyMissTeacher')
    if (t !== 'NPS' && !form.monthNumber) missing.push('surveyMissMonth')
    if (!form.openDate || !form.closeDate) missing.push('surveyMissDates')
    else if (form.openDate > form.closeDate) missing.push('surveyMissDateOrder')
    if (!form.questions.length) missing.push('surveyMissQuestion')
    else if (form.questions.some((q) => !q.textKz?.trim() || !q.textRu?.trim())) missing.push('surveyMissQuestionText')
    if (form.questions.some((q) => q.kind === 'CHOICE' && (q.options.length < 2 || q.options.some((o) => !o.textKz?.trim() || !o.textRu?.trim()))))
        missing.push('surveyMissOptions')
    if (t === 'NPS' && !form.questions.some((q) => q.kind === 'NPS')) missing.push('surveyMissNps')

    const submit = async () => {
        try {
            await save({
                id,
                body: {
                    ...form,
                    courseId: hasCourse(t) ? form.courseId : null,
                    subjectId: t === 'NPS' ? null : form.subjectId,
                    groupIds: hasCourse(t) ? form.groupIds : [],
                    monthNumber: t === 'NPS' ? null : form.monthNumber,
                    language: t === 'TEACHER' ? form.language || null : null,
                    subjectHeadUserId: t === 'SUBJECT_HEAD' ? form.subjectHeadUserId : null,
                    teacherUserId: t === 'TEACHER' ? form.teacherUserId : null,
                    questions: form.questions.map((q) => ({ ...q, required: true })),
                },
            }).unwrap()
            toast.success(formatMessage({ id: 'surveySaved' }))
            onSaved(t)
            onClose()
        } catch { /* общий обработчик */ }
    }

    const field = { size: 'small' as const, fullWidth: true }
    const kindHint: Record<SurveyKind, string> = {
        RATING: 'surveyKindHint_RATING', CHOICE: '', TEXT: 'surveyKindHint_TEXT', NPS: 'surveyKindHint_NPS',
    }

    return (
        <Dialog open onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ fontWeight: 700 }}>{formatMessage({ id: id ? 'surveyEdit' : 'surveyNew' })}</DialogTitle>
            <DialogContent>
                <Box sx={{ fontSize: '13px', fontWeight: 700, color: ui.sub, margin: '4px 0 8px' }}>{formatMessage({ id: 'surveyTypeLabel' })}</Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1, marginBottom: '12px' }}>
                    {SURVEY_TYPES.map((k) => (
                        <Box key={k} component="button" type="button" disabled={!!id} onClick={() => pickType(k)}
                            sx={{
                                textAlign: 'left', cursor: id ? 'default' : 'pointer', font: 'inherit', padding: '10px 12px',
                                borderRadius: ui.radius, border: `1px solid ${k === t ? ui.pink : ui.border}`,
                                background: k === t ? ui.pinkSoft : ui.card, opacity: id && k !== t ? 0.5 : 1,
                            }}>
                            <Box sx={{ fontWeight: 700, color: k === t ? ui.pinkDark : ui.ink, fontSize: '14px' }}>{formatMessage({ id: `surveyTypeTitle_${k}` })}</Box>
                            <Box sx={{ ...muted, marginTop: '2px' }}>{formatMessage({ id: `surveyWho_${k}` })}</Box>
                        </Box>
                    ))}
                </Box>
                <Box sx={{ ...sub, background: ui.surface, border: `1px solid ${ui.border}`, borderRadius: ui.radius, padding: '10px 12px', marginBottom: '14px' }}>
                    {formatMessage({ id: `surveyInfo_${t}` })}
                </Box>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5, paddingTop: '6px' }}>
                    <TextField {...field} label={formatMessage({ id: 'surveyTitleKz' })} value={form.titleKz} onChange={(e) => set({ titleKz: e.target.value })} />
                    <TextField {...field} label={formatMessage({ id: 'surveyTitleRu' })} value={form.titleRu} onChange={(e) => set({ titleRu: e.target.value })} />
                    {hasCourse(t) && (
                        <>
                            <TextField {...field} select disabled={locked} label={formatMessage({ id: 'surveyCourse' })} value={form.courseId ?? ''}
                                onChange={(e) => set({ courseId: Number(e.target.value) || null, subjectId: null, groupIds: [] })}>
                                {courses.map((c) => (
                                    <MenuItem key={c.courseId} value={c.courseId}>{(c as any)[`title${L}`] || c.titleRu}</MenuItem>
                                ))}
                            </TextField>
                            <TextField {...field} select disabled={locked || !course} label={formatMessage({ id: 'surveySubject' })} value={form.subjectId ?? 0}
                                onChange={(e) => set({ subjectId: Number(e.target.value) || null, groupIds: [] })}>
                                <MenuItem value={0}>{formatMessage({ id: 'surveyAllSubjects' })}</MenuItem>
                                {(course?.subjects ?? []).map((s) => (
                                    <MenuItem key={s.subjectId} value={s.subjectId}>{(s as any)[`title${L}`] || s.titleRu}</MenuItem>
                                ))}
                            </TextField>
                        </>
                    )}
                    {t === 'TEACHER' && (
                        <TextField {...field} select disabled={locked} label={formatMessage({ id: 'surveyTeacherLabel' })}
                            sx={{ gridColumn: '1 / -1' }} value={form.teacherUserId ?? ''}
                            helperText={!teachers.length ? formatMessage({ id: 'surveyNoTeachers' }) : undefined}
                            onChange={(e) => set({ teacherUserId: Number(e.target.value) || null })}>
                            {teachers.map((x) => (
                                <MenuItem key={x.userId} value={x.userId}>
                                    {`${x.lastName ?? ''} ${x.firstName ?? ''}`.trim() || `#${x.userId}`}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    {t === 'TEACHER' && (
                        <TextField {...field} select disabled={locked} label={formatMessage({ id: 'surveyLanguage' })} value={form.language ?? ''}
                            SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                            onChange={(e) => set({ language: e.target.value || null })}>
                            {LANGS.map((l) => <MenuItem key={l} value={l}>{l || 'KZ + RU'}</MenuItem>)}
                        </TextField>
                    )}
                    {hasCourse(t) && (
                        <TextField {...field} select disabled={locked || !course} label={formatMessage({ id: 'surveyGroups' })}
                            sx={{ gridColumn: t === 'TEACHER' ? undefined : '1 / -1' }}
                            value={form.groupIds}
                            SelectProps={{
                                multiple: true,
                                displayEmpty: true,
                                renderValue: (v) => {
                                    const ids = v as number[]
                                    return ids.length
                                        ? groups.filter((g) => ids.includes(g.id)).map((g) => g.title).join(', ')
                                        : formatMessage({ id: 'surveyAllGroups' })
                                },
                            }}
                            InputLabelProps={{ shrink: true }}
                            onChange={(e) => set({ groupIds: (e.target.value as unknown as number[]).filter(Boolean) })}>
                            {groups.length === 0 && (
                                <MenuItem disabled dense>{formatMessage({ id: 'surveyNoGroups' })}</MenuItem>
                            )}
                            {groups.map((g) => (
                                <MenuItem key={g.id} value={g.id} dense>
                                    <Checkbox size="small" checked={form.groupIds.includes(g.id)} sx={{ py: 0 }} />
                                    {g.title}
                                </MenuItem>
                            ))}
                        </TextField>
                    )}
                    {t === 'SUBJECT_HEAD' && (
                        <>
                            <TextField {...field} select disabled={locked} label={formatMessage({ id: 'surveySubject' })} value={form.subjectId ?? ''}
                                onChange={(e) => set({ subjectId: Number(e.target.value) || null, subjectHeadUserId: null })}>
                                {headSubjects.map((s) => <MenuItem key={s.id} value={s.id}>{s.title}</MenuItem>)}
                            </TextField>
                            <TextField {...field} select disabled={locked || !form.subjectId || !subjectHeads.length}
                                label={formatMessage({ id: 'surveyHeadLabel' })} value={form.subjectHeadUserId ?? ''}
                                helperText={form.subjectId && !subjectHeads.length ? formatMessage({ id: 'surveyNoHeads' }) : undefined}
                                onChange={(e) => set({ subjectHeadUserId: Number(e.target.value) || null })}>
                                {subjectHeads.map((h) => (
                                    <MenuItem key={`${h.userId}-${h.language}`} value={h.userId}>
                                        {`${h.firstName ?? ''} ${h.lastName ?? ''}`.trim() || `#${h.userId}`}{h.language ? ` · ${h.language}` : ''}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </>
                    )}
                    {t !== 'NPS' && (
                        <TextField {...field} select label={formatMessage({ id: 'surveyMonth' })} value={form.monthNumber ?? ''}
                            helperText={t === 'MENTOR' ? formatMessage({ id: 'surveyMonthHint' }) : undefined}
                            onChange={(e) => set({ monthNumber: Number(e.target.value) || null })}>
                            {MONTHS.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                        </TextField>
                    )}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
                        <TextField {...field} type="date" InputLabelProps={{ shrink: true }} label={formatMessage({ id: 'surveyOpenDate' })}
                            value={form.openDate ?? ''} onChange={(e) => set({ openDate: e.target.value || null })} />
                        <TextField {...field} type="date" InputLabelProps={{ shrink: true }} label={formatMessage({ id: 'surveyCloseDate' })}
                            value={form.closeDate ?? ''} onChange={(e) => set({ closeDate: e.target.value || null })} />
                    </Box>
                </Box>

                <Box sx={{ fontWeight: 700, color: ui.ink, margin: '20px 0 4px' }}>{formatMessage({ id: 'surveyQuestions' })}</Box>
                <Box sx={{ ...muted, marginBottom: '10px' }}>
                    {formatMessage({ id: locked ? 'surveyLockedHint' : !id && !touched ? 'surveySampleHint' : 'surveyAllRequiredHint' })}
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {form.questions.map((q, i) => (
                        <Box key={i} sx={{ border: `1px solid ${ui.border}`, borderRadius: ui.radius, padding: '12px', background: ui.surface }}>
                            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap' }}>
                                <Box sx={{ color: ui.muted, fontWeight: 700 }}>{i + 1}.</Box>
                                <TextField size="small" select disabled={locked} value={q.kind} sx={{ minWidth: 170 }}
                                    onChange={(e) => setQ(i, { ...emptyQuestion(e.target.value as SurveyKind), textKz: q.textKz, textRu: q.textRu })}>
                                    {kinds.concat(kinds.includes(q.kind) ? [] : [q.kind]).map((k) => (
                                        <MenuItem key={k} value={k}>{formatMessage({ id: `surveyKind_${k}` })}</MenuItem>
                                    ))}
                                </TextField>
                                <Pill tone="info" title={formatMessage({ id: 'surveyRequiredHint' })}>{formatMessage({ id: 'surveyRequired' })}</Pill>
                                {!locked && (
                                    <Box sx={{ marginLeft: 'auto', display: 'flex' }}>
                                        <IconButton size="small" onClick={() => moveQ(i, -1)} disabled={i === 0}
                                            aria-label={formatMessage({ id: 'surveyMoveUp' })}>↑</IconButton>
                                        <IconButton size="small" onClick={() => moveQ(i, 1)} disabled={i === form.questions.length - 1}
                                            aria-label={formatMessage({ id: 'surveyMoveDown' })}>↓</IconButton>
                                        <IconButton size="small" onClick={() => setQs((qs) => qs.filter((_, j) => j !== i))}
                                            aria-label={formatMessage({ id: 'surveyRemoveQuestion' })}>✕</IconButton>
                                    </Box>
                                )}
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                                <TextField {...field} multiline disabled={locked} label="KZ" value={q.textKz ?? ''} onChange={(e) => setQ(i, { textKz: e.target.value })} />
                                <TextField {...field} multiline disabled={locked} label="RU" value={q.textRu ?? ''} onChange={(e) => setQ(i, { textRu: e.target.value })} />
                            </Box>
                            {kindHint[q.kind] && <Box sx={{ ...muted, marginTop: '6px' }}>{formatMessage({ id: kindHint[q.kind] })}</Box>}
                            {q.kind === 'CHOICE' && (
                                <Box sx={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {q.options.map((o, k) => (
                                        <Box key={k} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr auto', sm: '1fr 1fr auto' }, gap: 1, alignItems: 'center' }}>
                                            <TextField {...field} disabled={locked} placeholder={`${formatMessage({ id: 'surveyOption' })} ${k + 1} KZ`} value={o.textKz ?? ''}
                                                onChange={(e) => setQ(i, { options: q.options.map((x, n) => (n === k ? { ...x, textKz: e.target.value } : x)) })} />
                                            <TextField {...field} disabled={locked} placeholder={`${formatMessage({ id: 'surveyOption' })} ${k + 1} RU`} value={o.textRu ?? ''}
                                                onChange={(e) => setQ(i, { options: q.options.map((x, n) => (n === k ? { ...x, textRu: e.target.value } : x)) })} />
                                            {!locked && (
                                                <IconButton size="small" disabled={q.options.length <= 2}
                                                    onClick={() => setQ(i, { options: q.options.filter((_, n) => n !== k) })}>✕</IconButton>
                                            )}
                                        </Box>
                                    ))}
                                    {!locked && q.options.length < 10 && (
                                        <Button size="small" sx={{ ...btn, alignSelf: 'flex-start', color: ui.pink }}
                                            onClick={() => setQ(i, { options: [...q.options, { textKz: '', textRu: '' }] })}>
                                            + {formatMessage({ id: 'surveyAddOption' })}
                                        </Button>
                                    )}
                                </Box>
                            )}
                        </Box>
                    ))}
                    {!form.questions.length && <Box sx={{ ...muted, textAlign: 'center', padding: '16px' }}>{formatMessage({ id: 'surveyNoQuestions' })}</Box>}
                </Box>
                {!locked && (
                    <Box sx={{ display: 'flex', gap: 1, marginTop: '12px', flexWrap: 'wrap' }}>
                        {kinds.map((k) => (
                            <Button key={k} size="small" variant="outlined" sx={{ ...btn, color: ui.ink, borderColor: ui.border }}
                                onClick={() => setQs((qs) => [...qs, emptyQuestion(k)])}>
                                + {formatMessage({ id: `surveyKind_${k}` })}
                            </Button>
                        ))}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, flexDirection: 'column', alignItems: 'stretch', gap: 1 }}>
                {missing.length > 0 && (
                    <Box sx={{ ...muted, color: ui.pinkDark }}>
                        {formatMessage({ id: 'surveyMissingPrefix' })}: {missing.map((m) => formatMessage({ id: m })).join(', ')}
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button sx={{ ...btn, color: ui.sub }} onClick={onClose}>{formatMessage({ id: 'cancel' })}</Button>
                    <Button variant="contained" sx={btnPink} disabled={missing.length > 0 || isLoading} onClick={submit}>
                        {formatMessage({ id: 'save' })}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    )
}

export default SurveysPage
